import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  Button,
  TextField,
  Typography,
  Box,
  Grid,
  Modal,
  IconButton,
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import Autocomplete from "@material-ui/lab/Autocomplete";
import LocationOnIcon from "@material-ui/icons/LocationOn";
import { MenuItem } from "@mui/material";
import DropdownFilter from "./DropdownFilter"; // Custom Dropdown Filter
import parse from "autosuggest-highlight/parse";
import debounce from "lodash/debounce";
import "../../assets/styles/FilterForm.css";

const GOOGLE_MAPS_API_KEY = "AIzaSyDXcuNu9hM04ec2v4lqzrfcrmIKjILZMRU";

// Utility to load the Google Maps script
const loadScript = (src, position, id) => {
  if (!position) return;
  if (document.getElementById(id)) return;
  const script = document.createElement("script");
  script.setAttribute("async", "");
  script.setAttribute("id", id);
  script.src = src;
  position.appendChild(script);
};

// A reusable multi-select field for the modal
const SelectField = ({ label, value, options, onChange }) => (
  <TextField
    select
    label={label}
    value={Array.isArray(value) ? value : []}
    onChange={onChange}
    SelectProps={{
      multiple: true,
      renderValue: (selected) =>
        selected.length === 0 ? "None" : selected.join(", "),
    }}
    variant="outlined"
    size="small"
    fullWidth
  >
    {options.map((option, index) => (
      <MenuItem key={index} value={option}>
        {option}
      </MenuItem>
    ))}
  </TextField>
);

// Data for the modal’s multi-select fields
const dropdownData = {
  statuses: ["Active", "Sold", "Sold Cond", "Suspended", "Terminated", "Expired"],
  propertyTypes: ["Detached", "Semi-Detached", "Townhouse", "Cottage", "Duplex", "Triplex"],
  squareFootRanges: ["< 700", "700-1100", "1100-1500", "1500-2000", "2000-2500", "5000+"],
  parkingSpaces: ["1", "2", "3", "4+"],
  acOptions: ["Central Air", "Wall Unit", "None"],
  basementOptions: ["Finished", "Unfinished", "Walk Out", "Apartment", "None"],
  kitchenOptions: ["1", "2", "3+"],
  ageOptions: ["New", "0-5", "6-15", "16-30", "31-50", "100+"],
};

const FilterForm = ({ filters, setFilters, resultCount, onSearchAddress }) => {
  const [inputValues, setInputValues] = useState(filters);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // For Google Places autocomplete
  const [inputValue, setInputValue] = useState("");
  const [options, setOptions] = useState([]);
  const [value, setValue] = useState(null);
  const loaded = useRef(false);
  const autocompleteService = useRef(null);

  // Beds/Baths multi-select options
  const bedOptions = ["0", "1", "2", "3", "4", "5+"];
  const bathOptions = ["0", "1", "2", "3", "4", "5+"];

  useEffect(() => {
    // Sync local state with parent filters
    setInputValues(filters);
  }, [filters]);

  // Load the Google Maps script if not already loaded
  useEffect(() => {
    if (typeof window !== "undefined" && !loaded.current) {
      if (!document.querySelector("#google-maps")) {
        loadScript(
          `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places`,
          document.querySelector("head"),
          "google-maps"
        );
      }
      loaded.current = true;
    }
  }, []);

  // Initialize the autocomplete service once google is ready
  useEffect(() => {
    if (window.google && !autocompleteService.current) {
      autocompleteService.current = new window.google.maps.places.AutocompleteService();
    }
  }, [window.google]);

  // Debounce for the Autocomplete
  const fetchPredictions = useMemo(
    () =>
      debounce((request, callback) => {
        if (autocompleteService.current) {
          autocompleteService.current.getPlacePredictions(request, callback);
        } else {
          callback([]);
        }
      }, 400),
    []
  );

  // Fetch predictions when inputValue changes
  useEffect(() => {
    let active = true;
    if (inputValue === "") {
      setOptions(value ? [value] : []);
      return undefined;
    }
    fetchPredictions(
      {
        input: inputValue,
        componentRestrictions: { country: "ca" },
      },
      (results) => {
        if (active) {
          let newOptions = [];
          if (value) newOptions = [value];
          if (results) newOptions = [...newOptions, ...results];
          setOptions(newOptions);
        }
      }
    );
    return () => {
      active = false;
    };
  }, [value, inputValue, fetchPredictions]);

  // Handle local changes to filter fields
  const handleChange = (name) => (event) => {
    const fieldVal = event.target.value;
    setInputValues((prev) => ({
      ...prev,
      [name]: fieldVal,
    }));
  };

  // Submit: geocode address (if any), then update parent
  const handleSubmit = async (event) => {
    event.preventDefault();
    if (value?.description) {
      try {
        const geocoder = new window.google.maps.Geocoder();
        const results = await geocoder.geocode({ address: value.description });
        if (results?.results?.[0]) {
          const { lat, lng } = results.results[0].geometry.location;
          onSearchAddress(lat(), lng());
        }
      } catch (err) {
        console.error("Geocoding error: ", err);
      }
    }
    // Update parent filters
    setFilters({ ...inputValues, address: value?.description || "" });
    setIsModalOpen(false);
  };

  const roundedResultCount = Math.floor(resultCount / 5) * 5;

  return (
    <Box p={2} borderBottom="1px solid #ddd" className="filterBarWrapper">
      {/* Main filter bar container */}
      <Box className="filterBarContainer">
        <Grid container spacing={2} alignItems="center">
          {/* Address / MLS Autocomplete */}
          <Grid item xs={3}>
            <Autocomplete
              getOptionLabel={(option) =>
                typeof option === "string" ? option : option.description
              }
              filterOptions={(x) => x}
              options={options}
              autoComplete
              includeInputInList
              filterSelectedOptions
              value={value}
              noOptionsText="No locations"
              size="small"
              onChange={(event, newValue) => {
                setOptions(newValue ? [newValue, ...options] : options);
                setValue(newValue);
              }}
              onInputChange={(event, newInputValue) => setInputValue(newInputValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Address or MLS® Number"
                  variant="outlined"
                  size="small"
                  fullWidth
                />
              )}
              renderOption={(option) => {
                const matches =
                  option.structured_formatting?.main_text_matched_substrings || [];
                const parts = parse(
                  option.structured_formatting.main_text,
                  matches.map((match) => [match.offset, match.offset + match.length])
                );
                return (
                  <Box>
                    <Grid container alignItems="center">
                      <Grid item sx={{ display: "flex", width: 44 }}>
                        <LocationOnIcon sx={{ color: "text.secondary" }} />
                      </Grid>
                      <Grid item>
                        {parts.map((part, index) => (
                          <span
                            key={index}
                            style={{ fontWeight: part.highlight ? "bold" : "normal" }}
                          >
                            {part.text}
                          </span>
                        ))}
                        <Typography variant="body2" color="text.secondary">
                          {option.structured_formatting.secondary_text}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>
                );
              }}
            />
          </Grid>

          {/* Min Price */}
          <Grid item xs={2}>
            <TextField
              label="Min Price"
              variant="outlined"
              size="small"
              fullWidth
              onChange={handleChange("minPrice")}
              value={inputValues.minPrice || ""}
            />
          </Grid>

          {/* Max Price */}
          <Grid item xs={2}>
            <TextField
              label="Max Price"
              variant="outlined"
              size="small"
              fullWidth
              onChange={handleChange("maxPrice")}
              value={inputValues.maxPrice || ""}
            />
          </Grid>

          {/* Beds multi-select using TextField with select */}
          <Grid item xs={1}>
            <TextField
              select
              label="Beds"
              value={inputValues.bedrooms || []}
              onChange={(e) =>
                setInputValues((prev) => ({ ...prev, bedrooms: e.target.value }))
              }
              SelectProps={{
                multiple: true,
                renderValue: (selected) =>
                  selected.length === 0 ? "None" : selected.join(", "),
                style: {paddingRight: 28}
              }}
              variant="outlined"
              size="small"
              fullWidth
              inputProps={{
                style: {textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap"},
              }}
            >
              {bedOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          {/* Baths multi-select using TextField with select */}
          <Grid item xs={1}>
            <TextField
              select
              label="Baths"
              value={inputValues.bathrooms || []}
              onChange={(e) =>
                setInputValues((prev) => ({ ...prev, bathrooms: e.target.value }))
              }
              SelectProps={{
                multiple: true,
                renderValue: (selected) =>
                  selected.length === 0 ? "None" : selected.join(", "),
              }}
              variant="outlined"
              size="small"
              fullWidth
            >
              {bathOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          {/* More Filters */}
          <Grid item xs={2}>
            <Button
              variant="contained"
              color="primary"
              size="small"
              fullWidth
              onClick={() => setIsModalOpen(true)}
            >
              More Filters
            </Button>
          </Grid>

          {/* Search Icon */}
          <Grid item xs={1} style={{ display: "flex", justifyContent: "center" }}>
            <IconButton size="small" color="primary" onClick={handleSubmit} className="searchIconButton">
              <SearchIcon />
            </IconButton>
          </Grid>
        </Grid>
      </Box>

      {/* Modal for more filters */}
      <Modal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        aria-labelledby="detailed-filter-modal"
        aria-describedby="modal-with-detailed-filter-options"
      >
        <Box
          p={3}
          sx={{
            position: "absolute",
            top: "80px",              // Push down from the top
            left: "50%",
            transform: "translateX(-50%)",
            width: "100%"
          }}
          m="auto"
          bgcolor="background.paper"
          maxWidth="500px"
          borderRadius="8px"
          boxShadow={3}
          style={{ outline: "none", overflow: "auto", maxHeight: "90vh" }}
        >
          <Typography variant="h4" id="detailed-filter-modal">
            Filter Options
          </Typography>
          <hr style={{ border: "1px solid #ddd", marginTop: "16px", marginBottom: "24px" }} />

          <Box mb={2} display="flex" justifyContent="flex-start" alignItems="center">
            <Typography variant="h6">Results:</Typography>
            <Box ml={2} className="resultsBadge">
              <Typography variant="h6" color="white">
                {roundedResultCount}+
              </Typography>
            </Box>
          </Box>

          {/* Bedrooms & Bathrooms in the modal */}
          <Grid container spacing={2}>
            <Grid item>
              <DropdownFilter
                label="Bedrooms"
                options={["0", "1", "2", "3", "4", "5"]}
                selectedOptions={inputValues.bedrooms || []}
                onSelect={(vals) =>
                  handleChange("bedrooms")({ target: { value: vals } })
                }
              />
            </Grid>
            <Grid item>
              <DropdownFilter
                label="Bathrooms"
                options={["0", "1", "2", "3", "4"]}
                selectedOptions={inputValues.bathrooms || []}
                onSelect={(vals) =>
                  handleChange("bathrooms")({ target: { value: vals } })
                }
              />
            </Grid>
          </Grid>

          {/* Search Radius */}
          <Typography variant="body1" style={{ marginTop: "16px" }}>
            Search Radius
          </Typography>
          <TextField
            label="Radius (km)"
            fullWidth
            onChange={handleChange("searchRadius")}
            value={inputValues.searchRadius || ""}
            variant="standard"
            margin="dense"
          />

          {/* Price Range */}
          <Typography variant="body1" style={{ marginTop: "16px" }}>
            Price Range
          </Typography>
          <Box display="flex" mb={1} alignItems="center">
            <TextField
              label="Min Price"
              onChange={handleChange("minPrice")}
              value={inputValues.minPrice || ""}
              fullWidth
              variant="standard"
              margin="dense"
            />
            <span style={{ margin: "0 8px", alignSelf: "center" }}>-</span>
            <TextField
              label="Max Price"
              onChange={handleChange("maxPrice")}
              value={inputValues.maxPrice || ""}
              fullWidth
              variant="standard"
              margin="dense"
            />
          </Box>

          {/* Last Updated Date Range */}
          <Typography variant="body1" style={{ marginTop: "16px" }}>
            Last Updated Date
          </Typography>
          <Box display="flex" mb={1} alignItems="center">
            <TextField
              label="From"
              fullWidth
              onChange={handleChange("lastUpdatedFrom")}
              value={inputValues.lastUpdatedFrom || ""}
              variant="standard"
              margin="dense"
            />
            <span style={{ margin: "0 8px", alignSelf: "center" }}>-</span>
            <TextField
              label="To"
              fullWidth
              onChange={handleChange("lastUpdatedTo")}
              value={inputValues.lastUpdatedTo || ""}
              variant="standard"
              margin="dense"
            />
          </Box>

          {/* Other multi-select fields in the modal */}
          <Box mb={2}>
            <SelectField
              label="Status"
              value={inputValues.Lsc}
              options={dropdownData.statuses}
              onChange={handleChange("Lsc")}
            />
          </Box>

          <Box mb={2}>
            <SelectField
              label="Square Footage"
              value={inputValues.Sqft}
              options={dropdownData.squareFootRanges}
              onChange={handleChange("Sqft")}
            />
          </Box>

          <Box mb={2}>
            <SelectField
              label="A/C"
              value={inputValues.A_c}
              options={dropdownData.acOptions}
              onChange={handleChange("A_c")}
            />
          </Box>

          <Box mb={2}>
            <SelectField
              label="Basement"
              value={inputValues.Bsmt1_out}
              options={dropdownData.basementOptions}
              onChange={handleChange("Bsmt1_out")}
            />
          </Box>

          <Box mb={2}>
            <SelectField
              label="Parking Spaces"
              value={inputValues.Tot_park_spcs}
              options={dropdownData.parkingSpaces}
              onChange={handleChange("Tot_park_spcs")}
            />
          </Box>

          <Box mb={2}>
            <SelectField
              label="Approx. Age"
              value={inputValues.Yr_built}
              options={dropdownData.ageOptions}
              onChange={handleChange("Yr_built")}
            />
          </Box>


          {/* Modal Footer */}
          <Box mt={3} display="flex" justifyContent="space-between">
            <Button variant="text" color="secondary" onClick={() => setIsModalOpen(false)}>
              Cancel
            </Button>
            <Button variant="contained" color="primary" onClick={handleSubmit}>
              Apply Filters
            </Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default FilterForm;
